import CtaSection from '@/components/CtaSection'
import Layout from '@/components/Layout'
import Seo from '@/components/Seo'
import { breadcrumbSchema, company, whatsappMessages } from '@/lib/site-data'
import {BadgeCheck, Building2, ShieldCheck, Users} from 'lucide-react'

const crumbs = breadcrumbSchema([
  { name: 'Início', path: '/' },
  { name: 'Sobre', path: '/sobre' },
])

const pillars = [
  {
    icon: Building2,
    title: 'Empresa registrada desde 2019',
    description: `${company.legalName}, CNPJ ${company.cnpj}, com sede em ${company.addressShort}.`,
  },
  {
    icon: Users,
    title: `Mais de ${company.clientsServed} clientes atendidos`,
    description: `Já vendemos mais de ${company.pagesSold} páginas nichadas do Instagram, de ${company.minFollowers} a ${company.maxFollowers} seguidores.`,
  },
  {
    icon: ShieldCheck,
    title: 'Verificação manual em todas as páginas',
    description: 'Toda página passa por checagem de compliance, histórico de conteúdo e ausência de shadowban antes da venda.',
  },
  {
    icon: BadgeCheck,
    title: 'Propriedade e garantia formalizadas',
    description: 'Somos proprietários das páginas que vendemos e oferecemos garantia de 7 dias, conforme o Código de Defesa do Consumidor.',
  },
]

function Sobre() {
  return (
    <Layout>
      <Seo
        title="Sobre a Muita Mídia | Empresa Especialista em Páginas do Instagram"
        description="Conheça a Muita Mídia LTDA, empresa registrada desde 2019, especialista em venda de páginas nichadas do Instagram com seguidores reais e transferência segura."
        path="/sobre"
        jsonLd={crumbs}
      />

      <section className="relative overflow-hidden bg-[#0B0919]">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(139,92,246,0.18),transparent_60%)]" />
        <div className="relative mx-auto max-w-4xl px-6 py-16 text-center sm:py-24">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs font-medium text-[#C4B5FD]">
            Sobre a Muita Mídia
          </span>
          <h1 className="mt-6 text-3xl font-bold leading-tight text-white sm:text-4xl">
            Especialistas em venda de páginas nichadas do Instagram desde 2019
          </h1>
          <p className="mt-5 text-base leading-relaxed text-zinc-400 sm:text-lg">
            A Muita Mídia LTDA nasceu para resolver um problema simples: crescer uma página do Instagram
            do zero consome tempo, dinheiro e energia que muita gente não tem disponível. Por isso,
            estruturamos um processo seguro para conectar pessoas e negócios a páginas já formadas,
            com verificação manual e transferência assistida em cada venda.
          </p>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-5xl px-6 py-16 sm:py-20">
          <div className="grid gap-6 sm:grid-cols-2">
            {pillars.map(pillar => (
              <div key={pillar.title} className="rounded-2xl border border-zinc-100 bg-zinc-50 p-6">
                <div className="inline-flex rounded-xl bg-[#8B5CF6]/10 p-3">
                  <pillar.icon className="h-6 w-6 text-[#7C3AED]" />
                </div>
                <h2 className="mt-4 text-lg font-semibold text-zinc-900">{pillar.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-zinc-600">{pillar.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0B0919]">
        <div className="mx-auto max-w-4xl px-6 py-16 sm:py-20">
          <h2 className="text-2xl font-bold text-white sm:text-3xl">Nossa forma de operar</h2>
          <div className="mt-6 space-y-4 text-base leading-relaxed text-zinc-400">
            <p>
              Trabalhamos com um portfólio próprio de páginas nichadas do Instagram, com faixas de {company.minFollowers} a {company.maxFollowers} seguidores,
              todas conectadas diretamente em nossa estrutura. Isso significa que não revendemos perfis de terceiros sem controle: cada página
              está sob nossa propriedade e gestão até o momento da transferência completa para o cliente.
            </p>
            <p>
              Antes de qualquer venda, cada perfil passa por uma verificação manual de compliance com as políticas da Meta e do Instagram,
              histórico de conteúdo, integridade da conta e ausência de shadowban ou restrições. Somente páginas aprovadas nesse processo
              entram no nosso portfólio.
            </p>
            <p>
              Depois da confirmação do pagamento, feito via Pix ou cartão de crédito em até 12x, a entrega é imediata pelo WhatsApp.
              Acompanhamos a transferência assistida — troca de e-mail, senha, autenticação em dois fatores e verificação de propriedade —
              até o cliente ter controle total do perfil, com garantia de 7 dias conforme o Código de Defesa do Consumidor.
            </p>
          </div>
        </div>
      </section>

      <CtaSection
        title="Quer conhecer nosso portfólio de páginas?"
        description="Fale com a equipe da Muita Mídia pelo WhatsApp e veja as opções disponíveis para o seu objetivo."
        message={whatsappMessages.contactCta}
        tone="light"
      />
    </Layout>
  )
}

export default Sobre
